export const databaseSystemsCourse = {
  id: 'database-systems',
  title: 'Database Systems',
  description: 'Comprehensive study of database design, implementation, and management including relational databases, SQL, normalization, and modern database technologies.',
  status: 'published',
  duration: '15 weeks',
  level: 'Intermediate',
  credits: 3,
  prerequisites: ['Data Structures and Algorithms', 'Programming Fundamentals'],
  learningObjectives: [
    'Design and implement relational database schemas',
    'Master SQL for data manipulation and querying',
    'Understand database normalization and optimization',
    'Implement database transactions and concurrency control',
    'Explore NoSQL databases and modern data storage solutions',
    'Apply database security and backup strategies'
  ],
  modules: [
    {
      id: 'database-fundamentals',
      title: 'Database Fundamentals and Data Models',
      description: 'Introduction to database concepts, data models, and database management systems.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'intro-databases',
          title: 'Introduction to Database Systems',
          content: `
# Introduction to Database Systems

## Learning Objectives
- Understand the purpose and benefits of database systems
- Compare different data models and database types
- Identify components of database management systems
- Analyze database system architectures

## What is a Database?
A database is an organized collection of structured information stored electronically in a computer system. It is managed by a Database Management System (DBMS).

## Database vs File System

### Problems with File Systems
- Data Redundancy: Same data stored in multiple files
- Data Inconsistency: Different versions of the same data
- Difficulty in accessing data: Need to write programs for each query
- Data isolation: Data scattered in various files
- Integrity problems: Difficult to enforce constraints
- Atomicity problems: Difficult to ensure all-or-nothing operations
- Concurrent access anomalies: Multiple users accessing simultaneously
- Security problems: Hard to provide user access control

### Advantages of DBMS
- Data independence: Applications independent of data storage
- Efficient data access: Optimized storage and retrieval
- Data integrity and security: Enforced constraints and access control
- Data administration: Centralized control
- Concurrent access: Multiple users can access simultaneously
- Crash recovery: Mechanisms to recover from failures
- Reduced application development time: Standard interfaces

## Data Models

### Hierarchical Model
- Tree-like structure with parent-child relationships
- Examples: IBM's IMS
- Limited flexibility for complex relationships

### Network Model
- Graph structure allowing multiple parent-child relationships
- More flexible than hierarchical
- Complex navigation

### Relational Model
- Data organized in tables (relations)
- Most widely used
- Strong mathematical foundation
- Examples: MySQL, PostgreSQL, Oracle

### Object-Oriented Model
- Data stored as objects with methods
- Good for complex data types
- Examples: ObjectDB, db4o

### NoSQL Models
- Document: JSON-like documents (MongoDB)
- Key-Value: Simple key-value pairs (Redis)
- Column-Family: Wide column stores (Cassandra)
- Graph: Nodes and edges (Neo4j)

## Database System Architecture

### Three-Schema Architecture
1. External Level (View Level)
   - Individual user views
   - Different users see different data
   - Data hiding and security

2. Conceptual Level (Logical Level)
   - Community user view
   - Describes what data is stored
   - Relationships among data

3. Internal Level (Physical Level)
   - How data is actually stored
   - Physical storage structures
   - Access paths and file organization

### Data Independence
- Logical Data Independence: Ability to change conceptual schema without changing external schemas
- Physical Data Independence: Ability to change internal schema without changing conceptual schema

## DBMS Components

### Storage Manager
- Interface between database and OS
- Manages allocation of disk space
- Data structures: files, pages, records

### Query Processor
- DDL Interpreter: Processes data definition statements
- DML Compiler: Translates DML statements into low-level instructions
- Query Evaluation Engine: Executes low-level instructions

### Transaction Manager
- Ensures ACID properties
- Manages concurrent access
- Handles recovery from failures

## Database Languages

### Data Definition Language (DDL)
- Define database schema
- CREATE, ALTER, DROP statements
- Specify constraints and relationships

### Data Manipulation Language (DML)
- Access and manipulate data
- INSERT, UPDATE, DELETE, SELECT
- Two types: Procedural and Non-procedural

### Data Control Language (DCL)
- Control access to database
- GRANT, REVOKE statements
- User privileges and security

## Database Users

### Database Administrators (DBA)
- Manage database system
- Performance tuning
- Security and backup

### Database Designers
- Design database schema
- Identify requirements
- Choose appropriate structures

### Application Programmers
- Write application programs
- Use database through APIs

### End Users
- Naive users: Use predefined applications
- Sophisticated users: Use query languages directly
- Specialized users: Use specialized database applications

## Applications
- Enterprise Information Systems
- Web Applications
- Data Warehousing and Analytics
- Content Management Systems
- Banking and Financial Systems
- Healthcare Information Systems
          `,
          interactiveExercises: [
            {
              id: 'database-concepts',
              title: 'Database Concepts Explorer',
              description: 'Implement basic database concepts and comparisons',
              initialCode: 'class DatabaseComparison {\n    constructor() {\n        this.fileSystems = [];\n        this.databases = [];\n    }\n\n    // Simulate file system problems\n    simulateFileSystemProblems() {\n        const problems = {\n            dataRedundancy: 0,\n            dataInconsistency: 0,\n            accessDifficulty: 0,\n            integrityIssues: 0\n        };\n        \n        // Implement simulation of file system problems\n        // Calculate scores for each problem type\n        \n        return problems;\n    }\n\n    // Compare DBMS advantages\n    evaluateDBMSAdvantages() {\n        const advantages = {\n            dataIndependence: false,\n            efficientAccess: false,\n            dataIntegrity: false,\n            concurrentAccess: false,\n            crashRecovery: false\n        };\n        \n        // Implement evaluation of DBMS advantages\n        \n        return advantages;\n    }\n}',
              solution: 'class DatabaseComparison {\n    constructor() {\n        this.fileSystems = [];\n        this.databases = [];\n    }\n    \n    simulateFileSystemProblems() {\n        const problems = {\n            dataRedundancy: this.calculateRedundancy(),\n            dataInconsistency: this.calculateInconsistency(),\n            accessDifficulty: this.calculateAccessDifficulty(),\n            integrityIssues: this.calculateIntegrityIssues()\n        };\n        \n        return problems;\n    }\n    \n    calculateRedundancy() {\n        // Simulate having same customer data in multiple files\n        const customerFiles = [\"orders.txt\", \"billing.txt\", \"support.txt\"];\n        const duplicateDataPercentage = customerFiles.length * 0.3; // 30% duplicate per file\n        return Math.round(duplicateDataPercentage * 100) / 100;\n    }\n    \n    evaluateDBMSAdvantages() {\n        const advantages = {\n            dataIndependence: true,\n            efficientAccess: true,\n            dataIntegrity: true,\n            concurrentAccess: true,\n            crashRecovery: true\n        };\n        \n        return advantages;\n    }\n}',
              hints: [
                'File systems have redundancy and consistency issues',
                'DBMS provides data independence and integrity',
                'Different data models suit different use cases',
                'Consider scalability, consistency, and query complexity'
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'sql-basics',
      title: 'SQL Fundamentals',
      description: 'Master SQL querying, joins, subqueries, and database operations.',
      duration: '4 weeks',
      lessons: [
        {
          id: 'sql-queries',
          title: 'SQL Query Fundamentals',
          content: `
# SQL Query Fundamentals

## Learning Objectives
- Master basic SQL SELECT statements
- Understand WHERE clauses and filtering
- Apply aggregate functions and GROUP BY
- Write complex joins and subqueries

## SQL Basics

### SELECT Statement Structure
\`\`\`sql
SELECT column1, column2, ...
FROM table_name
WHERE condition
GROUP BY column
HAVING condition
ORDER BY column
LIMIT number;
\`\`\`

### Basic Queries
\`\`\`sql
-- Select all columns
SELECT * FROM students;

-- Select specific columns
SELECT name, gpa FROM students;

-- Filter with WHERE
SELECT name FROM students WHERE gpa > 3.5;

-- Sort results
SELECT name, gpa FROM students ORDER BY gpa DESC;
\`\`\`

### Filtering Data
\`\`\`sql
-- Comparison operators
SELECT * FROM students WHERE gpa >= 3.0;

-- Pattern matching
SELECT * FROM students WHERE name LIKE 'John%';

-- Range queries
SELECT * FROM students WHERE gpa BETWEEN 3.0 AND 4.0;

-- Set membership
SELECT * FROM courses WHERE department IN ('CS', 'MATH', 'PHYS');
\`\`\`

### Aggregate Functions
\`\`\`sql
-- Count records
SELECT COUNT(*) FROM students;

-- Calculate averages
SELECT AVG(gpa) FROM students;

-- Find min/max
SELECT MIN(gpa), MAX(gpa) FROM students;

-- Group by department
SELECT department, COUNT(*), AVG(credits)
FROM courses
GROUP BY department;
\`\`\`

### Joins
\`\`\`sql
-- Inner join
SELECT s.name, c.title, e.grade
FROM students s
JOIN enrollments e ON s.id = e.student_id
JOIN courses c ON e.course_id = c.id;

-- Left outer join
SELECT s.name, e.grade
FROM students s
LEFT JOIN enrollments e ON s.id = e.student_id;
\`\`\`

### Subqueries
\`\`\`sql
-- Scalar subquery
SELECT name FROM students 
WHERE gpa > (SELECT AVG(gpa) FROM students);

-- EXISTS subquery
SELECT name FROM students s
WHERE EXISTS (SELECT 1 FROM enrollments e WHERE e.student_id = s.id);
\`\`\`

## Applications
- Data analysis and reporting
- Business intelligence
- Web application backends
- Data warehousing
          `,
          interactiveExercises: [
            {
              id: 'sql-practice',
              title: 'SQL Query Practice',
              description: 'Practice writing SQL queries for various scenarios',
              initialCode: '-- Write SQL queries for the following scenarios:\n-- Tables: students(id, name, gpa), courses(id, title, credits), enrollments(student_id, course_id, grade)\n\n-- 1. Find all students with GPA above 3.5\nSELECT name FROM students WHERE gpa > 3.5;\n\n-- 2. Count students by grade in each course\n-- Your query here\n\n-- 3. Find students who are enrolled in CS courses\n-- Your query here',
              solution: '-- 1. Find all students with GPA above 3.5\nSELECT name FROM students WHERE gpa > 3.5;\n\n-- 2. Count students by grade in each course\nSELECT c.title, e.grade, COUNT(*) as student_count\nFROM courses c\nJOIN enrollments e ON c.id = e.course_id\nGROUP BY c.title, e.grade\nORDER BY c.title, e.grade;\n\n-- 3. Find students who are enrolled in CS courses\nSELECT DISTINCT s.name\nFROM students s\nJOIN enrollments e ON s.id = e.student_id\nJOIN courses c ON e.course_id = c.id\nWHERE c.title LIKE \"CS%\";',
              hints: [
                'Use JOIN to combine data from multiple tables',
                'GROUP BY is used with aggregate functions',
                'Use DISTINCT to eliminate duplicates',
                'LIKE operator supports pattern matching with % wildcard'
              ]
            }
          ]
        }
      ]
    }
  ],
  assessments: [
    {
      id: 'database-midterm',
      title: 'Database Systems Midterm Exam',
      type: 'exam',
      description: 'Comprehensive exam covering database fundamentals, SQL, and design principles',
      questions: [
        {
          id: 'sql-queries',
          type: 'problem-solving',
          question: 'Write SQL queries for a university database with tables Students(id, name, major, gpa), Courses(id, title, credits), and Enrollments(student_id, course_id, grade, semester). Write queries to: (a) Find students with GPA > 3.5, (b) List courses with their enrollment counts, (c) Find students who took CS courses but never got an A.',
          points: 30,
          rubric: 'Correct SQL syntax (10 pts), proper joins and conditions (10 pts), query optimization awareness (10 pts)'
        },
        {
          id: 'database-design',
          type: 'problem-solving',
          question: 'Design a database schema for a library system managing books, authors, borrowers, and loans. Include tables, primary keys, foreign keys, and constraints.',
          points: 25,
          rubric: 'Table design (8 pts), key identification (8 pts), constraint specification (9 pts)'
        },
        {
          id: 'normalization',
          type: 'problem-solving',
          question: 'Given a table with functional dependencies, normalize it to 3NF. Explain each step and identify any violations.',
          points: 20,
          rubric: 'Functional dependency analysis (7 pts), normalization steps (8 pts), explanation quality (5 pts)'
        }
      ],
      timeLimit: 120,
      passingScore: 70
    },
    {
      id: 'database-project',
      title: 'Database Design and Implementation Project',
      type: 'project',
      description: 'Complete database system design, implementation, and optimization project',
      requirements: [
        'Design conceptual model using ER diagrams',
        'Implement normalized relational schema',
        'Create comprehensive SQL queries and procedures',
        'Implement transaction processing and error handling',
        'Develop database security and backup strategies',
        'Performance testing and optimization analysis'
      ],
      rubric: {
        'Database Design': 25,
        'SQL Implementation': 25,
        'Normalization': 15,
        'Performance Analysis': 20,
        'Documentation': 15
      },
      timeLimit: null,
      passingScore: 75
    }
  ],
  resources: [
    {
      id: 'silberschatz-database',
      title: 'Database System Concepts',
      author: 'Silberschatz, Korth, Sudarshan',
      type: 'textbook',
      url: 'https://www.db-book.com/',
      description: 'Comprehensive database systems textbook covering theory and practice'
    },
    {
      id: 'mysql-documentation',
      title: 'MySQL Documentation',
      type: 'documentation',
      url: 'https://dev.mysql.com/doc/',
      description: 'Official MySQL database documentation and tutorials'
    },
    {
      id: 'postgresql-tutorial',
      title: 'PostgreSQL Tutorial',
      type: 'online-course',
      url: 'https://www.postgresqltutorial.com/',
      description: 'Comprehensive PostgreSQL tutorial with examples'
    }
  ],
  courseFormat: 'hybrid',
  gradingScale: {
    'A': { min: 90, max: 100 },
    'B': { min: 80, max: 89 },
    'C': { min: 70, max: 79 },
    'D': { min: 60, max: 69 },
    'F': { min: 0, max: 59 }
  },
  policies: {
    attendance: 'Regular attendance required for lab sessions. More than 2 unexcused absences may result in grade reduction.',
    lateWork: 'Late assignments penalized 15% per day. Database projects have hard deadlines.',
    academicIntegrity: 'Individual work required. Collaboration allowed on conceptual discussions but not code implementation.',
    makeup: 'Makeup exams for documented emergencies only. Alternative assignments may be provided.',
    technology: 'Access to database systems required. Course provides cloud database access for all students.',
    communication: 'Course forum monitored daily. Email response within 24 hours during weekdays.',
    accommodations: 'Students requiring accommodations should coordinate with Disability Services and inform instructor.'
  }
};